// Playoff bracket builder.
//
// Two suggestions are offered (within-division 1v4 / 2v3, or cross-division
// 1v16 seeding) but EVERY matchup is a dropdown, so you can build the
// bracket however you want before locking it in.
//
// Structure:
//   Oct 17  Champions Pointe   Round of 16 (4 foursomes) -> QF (2 foursomes)
//   Oct 18  Covered Bridge     SF (2 twosomes, front 9)  -> Final (back 9)

import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Trophy, Wand2, AlertTriangle, ChevronRight, Save, RotateCcw, Info } from 'lucide-react';
import DivisionBadge from './DivisionBadge';
import ReadOnlyNotice from './ReadOnlyNotice';
import MatchCard from './MatchCard';
import { DIVISIONS, PLAYOFF_QUALIFIERS_PER_DIVISION } from '../utils/constants';
import { formatTime, formatDate } from '../utils/helpers';
import {
  suggestRoundOf16, suggestCrossDivision, BRACKET_SHAPE,
  roundWinners, BRACKET_ROUND_LABELS,
} from '../utils/playoffs';
import { playoffQualifiers, unresolvedTiebreaks } from '../utils/scoring';

export default function PlayoffsTab({
  standings, matches, golfers, teeSets, courses, seasonDates, isAdmin,
  onBuildBracket, onSaveMatch, onAdvanceRound, onClearBracket, onChangeResult, onChangeTee,
}) {
  const [busy, setBusy] = useState(false);
  const [edits, setEdits] = useState({}); // matchId -> pairings array

  const quals = useMemo(() => playoffQualifiers(standings), [standings]);
  const blockers = useMemo(() => unresolvedTiebreaks(standings), [standings]);
  const playoffMatches = useMemo(
    () => (matches || []).filter(m => m.phase === 'playoff'),
    [matches]);

  const qualifierIds = useMemo(
    () => DIVISIONS.flatMap(d => (quals[d] || []).map(r => r.golfer.id)),
    [quals]);

  const gmap = useMemo(() => new Map((golfers || []).map(g => [g.id, g])), [golfers]);

  const byRound = useMemo(() => {
    const m = {};
    playoffMatches.forEach(x => {
      const r = x.bracket_round || 0;
      m[r] = m[r] || [];
      m[r].push(x);
    });
    Object.values(m).forEach(l => l.sort((a, b) => (a.bracket_slot || 0) - (b.bracket_slot || 0)));
    return m;
  }, [playoffMatches]);

  const pairingsOf = (m) => edits[m.id] || m.pairings;
  const isDirty = (m) => !!edits[m.id];

  const setSlot = (m, index, golferId) => {
    const next = [...pairingsOf(m)];
    next[index] = golferId;
    setEdits(e => ({ ...e, [m.id]: next }));
  };

  const saveMatchup = async (m) => {
    const next = pairingsOf(m);
    const players = next.filter(Boolean);
    await onSaveMatch({
      ...m,
      pairings: next,
      group_players: players,
      results: Object.fromEntries(players.map(id => [id, m.results?.[id] || 'pending'])),
    });
    setEdits(e => { const c = { ...e }; delete c[m.id]; return c; });
  };

  const build = async (kind) => {
    const suggestion = kind === 'cross'
      ? suggestCrossDivision(quals)
      : suggestRoundOf16(quals);
    if (suggestion.length !== 8) {
      alert('Need 4 qualifiers in every division before the bracket can be built.');
      return;
    }
    const msg = playoffMatches.length > 0
      ? 'This rebuilds the whole bracket and clears any playoff results. Continue?'
      : 'Build the playoff bracket?';
    if (!window.confirm(msg)) return;
    setBusy(true);
    try { await onBuildBracket(suggestion); } finally { setBusy(false); }
  };

  const readyToBuild = DIVISIONS.every(d => (quals[d] || []).length === PLAYOFF_QUALIFIERS_PER_DIVISION);

  return (
    <div className="mt-4 space-y-6">
      {!isAdmin && <ReadOnlyNotice>Bracket is view-only. Unlock admin to build or edit it.</ReadOnlyNotice>}

      {/* ---- Qualifiers ---- */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Trophy className="h-5 w-5" />Playoff field</CardTitle>
          <CardDescription>
            Top {PLAYOFF_QUALIFIERS_PER_DIVISION} from each division. 16 players, single elimination.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {blockers.length > 0 && (
            <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
              <p className="flex items-center gap-1.5 font-medium">
                <AlertTriangle className="h-4 w-4" />
                {blockers.length} unresolved tie{blockers.length === 1 ? '' : 's'} for a final playoff spot.
              </p>
              <ul className="mt-1 list-inside list-disc">
                {blockers.map((b, i) => (
                  <li key={i}>{b.division}: {b.golfer.name} (rank {b.rank})</li>
                ))}
              </ul>
              <p className="mt-1">Settle these on the Standings tab (chip-off / 40yd dash) before building the bracket.</p>
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {DIVISIONS.map(d => (
              <div key={d} className="rounded-lg border border-gray-200 p-2">
                <DivisionBadge division={d} />
                <ol className="mt-2 space-y-1 text-sm">
                  {(quals[d] || []).map(r => (
                    <li key={r.golfer.id} className="flex items-center justify-between gap-2">
                      <span><span className="mr-1 text-gray-400">{r.rank}.</span>{r.golfer.name}</span>
                      <span className="text-xs text-gray-500">{r.points} pts</span>
                    </li>
                  ))}
                  {(quals[d] || []).length === 0 && (
                    <li className="text-xs text-gray-400">no results yet</li>
                  )}
                </ol>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ---- Build ---- */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Wand2 className="h-5 w-5" />Build the bracket</CardTitle>
            <CardDescription>
              Pick a starting suggestion, then edit any matchup below. Nothing is locked.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Button disabled={!readyToBuild || busy} onClick={() => build('division')}>
                <Wand2 className="mr-2 h-4 w-4" /> Seed within divisions (1v4, 2v3)
              </Button>
              <Button variant="outline" disabled={!readyToBuild || busy} onClick={() => build('cross')}>
                <Wand2 className="mr-2 h-4 w-4" /> Seed across divisions (1v16)
              </Button>
              {playoffMatches.length > 0 && (
                <Button variant="ghost" disabled={busy}
                  onClick={async () => {
                    if (!window.confirm('Delete the entire bracket and all playoff results?')) return;
                    setBusy(true);
                    try { await onClearBracket(); } finally { setBusy(false); }
                  }}>
                  <RotateCcw className="mr-2 h-4 w-4" /> Clear bracket
                </Button>
              )}
            </div>
            {!readyToBuild && (
              <p className="text-xs text-amber-700">
                Each division needs 4 ranked qualifiers. Enter regular-season results first.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ---- Bracket ---- */}
      {playoffMatches.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          No bracket yet.
        </div>
      ) : (
        BRACKET_SHAPE.map(shape => {
          const roundMatches = byRound[shape.round] || [];
          if (roundMatches.length === 0) return null;
          const winners = roundWinners(playoffMatches, shape.round);
          const nextExists = !!byRound[shape.round + 1];

          return (
            <Card key={shape.round}>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {shape.round === 4 && <Trophy className="h-5 w-5 text-amber-500" />}
                      {BRACKET_ROUND_LABELS[shape.round]}
                    </CardTitle>
                    <CardDescription>
                      {shape.matches} match{shape.matches === 1 ? '' : 'es'} &middot;{' '}
                      {roundMatches[0] && (
                        <>
                          {formatDate(roundMatches[0].match_date)} &middot;{' '}
                          {(courses || []).find(c => c.id === roundMatches[0].course_id)?.name} &middot;{' '}
                          {roundMatches[0].nine === 'B9' ? 'back 9' : 'front 9'} &middot;{' '}
                          {roundMatches.map(m => formatTime(m.tee_time)).join(', ')}
                        </>
                      )}
                    </CardDescription>
                  </div>
                  {isAdmin && nextExists && (
                    <Button
                      size="sm" variant="outline"
                      disabled={!winners.complete || busy}
                      title={winners.complete
                        ? `Send the ${shape.matches} winners to ${BRACKET_ROUND_LABELS[shape.round + 1]}`
                        : 'All matches in this round need a winner first'}
                      onClick={async () => {
                        setBusy(true);
                        try { await onAdvanceRound(shape.round); } finally { setBusy(false); }
                      }}
                    >
                      Advance winners <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {winners.pending.length > 0 && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                    {winners.pending.map((p, i) => (
                      <div key={i} className="flex items-start gap-1.5">
                        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                        <span>{formatTime(p.match.tee_time)}: {p.reason}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Matchup editors */}
                {isAdmin && (
                  <div className="space-y-2">
                    {roundMatches.map(m => {
                      const pr = pairingsOf(m);
                      const pairs = [];
                      for (let i = 0; i < pr.length; i += 2) pairs.push([i, i + 1]);
                      return (
                        <div key={m.id} className="rounded-md border border-gray-200 bg-gray-50 p-2">
                          <div className="mb-1 flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-600">
                              {formatTime(m.tee_time)} {m.notes ? `- ${m.notes}` : ''}
                            </span>
                            <div className="flex items-center gap-2">
                              {isDirty(m) && <span className="text-xs text-amber-600">unsaved</span>}
                              <Button size="sm" disabled={!isDirty(m)} onClick={() => saveMatchup(m)}>
                                <Save className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                          {pairs.map(([iA, iB]) => (
                            <div key={iA} className="mb-1 flex items-center gap-2">
                              <select
                                className="h-8 flex-1 rounded-md border border-gray-300 bg-white px-2 text-xs"
                                value={pr[iA] || ''}
                                onChange={e => setSlot(m, iA, e.target.value)}
                              >
                                <option value="">TBD</option>
                                {qualifierIds.map(id => (
                                  <option key={id} value={id}>
                                    {gmap.get(id)?.name} ({gmap.get(id)?.division})
                                  </option>
                                ))}
                              </select>
                              <span className="text-xs font-semibold text-gray-400">vs</span>
                              <select
                                className="h-8 flex-1 rounded-md border border-gray-300 bg-white px-2 text-xs"
                                value={pr[iB] || ''}
                                onChange={e => setSlot(m, iB, e.target.value)}
                              >
                                <option value="">TBD</option>
                                {qualifierIds.map(id => (
                                  <option key={id} value={id}>
                                    {gmap.get(id)?.name} ({gmap.get(id)?.division})
                                  </option>
                                ))}
                              </select>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Playable cards with strokes + result entry */}
                {roundMatches.map(m => (
                  <MatchCard
                    key={`card_${m.id}`}
                    match={m}
                    golfers={golfers}
                    teeSets={teeSets}
                    courses={courses}
                    isAdmin={isAdmin}
                    onChangeResult={onChangeResult}
                    onChangeTee={onChangeTee}
                  />
                ))}
              </CardContent>
            </Card>
          );
        })
      )}

      <div className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <p><strong>Playoff matches must have a winner.</strong> A halved match blocks advancement -
          play extra holes and record the winner.</p>
          <p className="mt-1">A forfeit advances the opponent automatically.</p>
          <p className="mt-1">Playoff results do not affect regular-season points or standings.</p>
        </div>
      </div>
    </div>
  );
}
